import { ApprovalStatus, GlobalProduct } from '@bofrak-backend/shared';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { globalProductDb, isCatalogStale } from '../db';
import {
  GLOBAL_PRODUCT_CATALOG_ID,
  GlobalProductCatalogSyncResponse,
  GlobalProductSearchResponse,
  GlobalProductSyncMetadata,
} from '../types';
import GlobalProductSearchWorker from '../workers/global-product-search.worker?worker';
import GlobalProductSyncWorker from '../workers/global-product-sync.worker?worker';

interface CatalogContextValue {
  products: GlobalProduct[];
  metadata?: GlobalProductSyncMetadata;
  isInitialLoad: boolean;
  syncProgress: number;
  /** Full sync now (e.g. a Refresh button, or right before an import). */
  refresh: () => Promise<void>;
  /** Syncs only when the local copy is older than `maxAgeMs`. */
  ensureFresh: () => Promise<void>;
}

const GlobalProductCatalogContext = createContext<CatalogContextValue | null>(
  null,
);

/** A local catalog younger than this is used without asking the API. */
export const GLOBAL_PRODUCT_CATALOG_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface GlobalProductCatalogProviderProps extends PropsWithChildren {
  apiEndpoint: string;
  accessToken?: string;
  /** Sync on mount when the local copy is stale. Default true. */
  autoSync?: boolean;
  maxAgeMs?: number;
}

export const GlobalProductCatalogProvider = ({
  apiEndpoint,
  accessToken = '',
  autoSync = true,
  maxAgeMs = GLOBAL_PRODUCT_CATALOG_MAX_AGE_MS,
  children,
}: GlobalProductCatalogProviderProps) => {
  const products =
    useLiveQuery(() => globalProductDb.globalProducts.toArray(), [], []) ?? [];
  const metadata = useLiveQuery(
    () =>
      globalProductDb.globalProductSyncMetadata.get(
        GLOBAL_PRODUCT_CATALOG_ID,
      ),
    [],
  );
  const workerRef = useRef<Worker | null>(null);
  const pendingRef = useRef<
    Map<string, { resolve: () => void; reject: (error: Error) => void }>
  >(new Map());
  const activePromiseRef = useRef<Promise<void> | null>(null);
  const [syncProgress, setSyncProgress] = useState(0);

  const ensureWorker = useCallback(() => {
    if (workerRef.current) return workerRef.current;
    const worker = new GlobalProductSyncWorker();
    worker.onmessage = (
      event: MessageEvent<GlobalProductCatalogSyncResponse>,
    ) => {
      const message = event.data;
      if (message.type === 'SYNC_PROGRESS') {
        setSyncProgress(message.loaded);
        return;
      }
      const pending = pendingRef.current.get(message.requestId);
      if (!pending) return;
      pendingRef.current.delete(message.requestId);
      activePromiseRef.current = null;
      if (message.type === 'SYNC_COMPLETE') {
        setSyncProgress(message.loaded);
        pending.resolve();
      } else {
        pending.reject(new Error(message.error));
      }
    };
    workerRef.current = worker;
    return worker;
  }, []);

  const refresh = useCallback((): Promise<void> => {
    if (activePromiseRef.current) return activePromiseRef.current;
    const worker = ensureWorker();
    const requestId = crypto.randomUUID();
    const promise = new Promise<void>((resolve, reject) => {
      pendingRef.current.set(requestId, { resolve, reject });
      worker.postMessage({
        type: 'SYNC',
        requestId,
        apiEndpoint,
        accessToken,
      });
    });
    activePromiseRef.current = promise;
    return promise;
  }, [accessToken, apiEndpoint, ensureWorker]);

  const ensureFresh = useCallback(async (): Promise<void> => {
    const current = await globalProductDb.globalProductSyncMetadata.get(
      GLOBAL_PRODUCT_CATALOG_ID,
    );
    if (!isCatalogStale(current, maxAgeMs)) return;
    return refresh();
  }, [maxAgeMs, refresh]);

  useEffect(() => {
    ensureWorker();
    if (autoSync) ensureFresh().catch(() => undefined);
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
      for (const pending of pendingRef.current.values()) {
        pending.reject(new Error('Global Product sync cancelled'));
      }
      pendingRef.current.clear();
      activePromiseRef.current = null;
    };
  }, [autoSync, ensureFresh, ensureWorker]);

  const value = useMemo<CatalogContextValue>(
    () => ({
      products,
      metadata,
      isInitialLoad: products.length === 0 && metadata?.status !== 'error',
      syncProgress,
      refresh,
      ensureFresh,
    }),
    [ensureFresh, metadata, products, refresh, syncProgress],
  );

  return (
    <GlobalProductCatalogContext.Provider value={value}>
      {children}
    </GlobalProductCatalogContext.Provider>
  );
};

export const useGlobalProductCatalog = (): CatalogContextValue => {
  const value = useContext(GlobalProductCatalogContext);
  if (!value) {
    throw new Error(
      'useGlobalProductCatalog must be used inside GlobalProductCatalogProvider',
    );
  }
  return value;
};

/**
 * The catalog for a page that shows it: syncs on mount only when the local
 * copy is older than the provider's `maxAgeMs` (so reloads cost nothing).
 */
export const useFreshGlobalProductCatalog = (): CatalogContextValue => {
  const catalog = useGlobalProductCatalog();
  const { ensureFresh } = catalog;
  useEffect(() => {
    ensureFresh().catch(() => undefined);
  }, [ensureFresh]);
  return catalog;
};

export const useGlobalProductSearch = ({
  query,
  approvalStatus,
  parentsOnly = true,
}: {
  query: string;
  approvalStatus?: ApprovalStatus;
  parentsOnly?: boolean;
}) => {
  const { products, metadata } = useGlobalProductCatalog();
  const [productIds, setProductIds] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string>();
  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef('');

  useEffect(() => {
    const worker = new GlobalProductSearchWorker();
    worker.onmessage = (event: MessageEvent<GlobalProductSearchResponse>) => {
      const message = event.data;
      if (message.requestId !== requestIdRef.current) return;
      setIsSearching(false);
      if (message.type === 'SEARCH_COMPLETE') {
        setProductIds(message.productIds);
        setError(undefined);
      } else {
        setError(message.error);
      }
    };
    workerRef.current = worker;
    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!workerRef.current) return;
    const timeout = window.setTimeout(() => {
      const requestId = crypto.randomUUID();
      requestIdRef.current = requestId;
      setIsSearching(true);
      workerRef.current?.postMessage({
        type: 'SEARCH',
        requestId,
        query,
        approvalStatus,
        parentsOnly,
      });
    }, 200);
    return () => window.clearTimeout(timeout);
  }, [approvalStatus, metadata?.revision, parentsOnly, query]);

  const productsById = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products],
  );
  const results = useMemo(
    () =>
      productIds
        .map((id) => productsById.get(id))
        .filter(Boolean) as GlobalProduct[],
    [productIds, productsById],
  );

  return { products: results, isSearching, error };
};